import { ButtonGroup, Button, InlineStack, Box } from '@shopify/polaris';
import {
  MobileIcon,
  TabletIcon,
  ViewIcon,
  RefreshIcon,
  RotateLeftIcon,
  RotateRightIcon,
  SelectIcon,
  CursorIcon,
} from '@shopify/polaris-icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  usePreviewStore,
  type PreviewDevice,
  type PreviewOrientation,
} from '@/state/previewStore';
import { useSelectionStore } from '@/state/selectionStore';
import { useStagedDraft, requestTemplateSwitch } from '@/queries/stagedDraft';

const PREVIEW_BASE = '/preview/';
function previewSrc(template: string, previewHandle: string | null): string {
  // PreviewApp reads `template` from the URL search params (templateParam())
  // and the specific collection/product to render from `previewHandle` —
  // decoupled so the editor stays on the generic template (Shopify model).
  const params = new URLSearchParams({ template });
  if (previewHandle) params.set('previewHandle', previewHandle);
  return `${PREVIEW_BASE}?${params.toString()}`;
}

type DeviceKey = Exclude<PreviewDevice, 'both'>;

interface DevicePortraitSpec {
  inner: { w: number; h: number };
  bezel: number;
  radius: number;
  label: string;
}

// Portrait specs — flipped to landscape at render time when needed.
const DEVICES_PORTRAIT: Record<DeviceKey, DevicePortraitSpec> = {
  phone: { inner: { w: 393, h: 852 }, bezel: 14, radius: 36, label: 'iPhone 15 Pro' },
  tablet: { inner: { w: 834, h: 1194 }, bezel: 18, radius: 22, label: 'iPad 11"' },
};

function specFor(device: DeviceKey, orientation: PreviewOrientation): DevicePortraitSpec {
  const base = DEVICES_PORTRAIT[device];
  if (orientation === 'portrait') return base;
  return {
    ...base,
    inner: { w: base.inner.h, h: base.inner.w },
  };
}

/**
 * The mobile RNW preview app (PreviewApp.tsx) maintains its own viewport
 * state initialized to iPhone defaults (393x852). It only updates that
 * state when the parent sends a `viewport:set` postMessage. Without it,
 * the iPad/landscape preview renders inside a 393-wide device wrapper
 * with overflow:hidden.
 *
 * Handshake:
 *  1. Parent renders <iframe>.
 *  2. RNW app boots and posts `{v:1, kind:'ready'}` to parent.
 *  3. Parent responds with `viewport:set {width, height}`.
 *  4. RNW app updates its viewport state and re-renders the device shell.
 */
function postToIframe(ref: React.RefObject<HTMLIFrameElement | null>, msg: object) {
  ref.current?.contentWindow?.postMessage(msg, window.location.origin);
}

function DeviceFrame({
  device,
  orientation,
  refreshKey,
  scale,
  template,
  previewHandle,
}: {
  device: DeviceKey;
  orientation: PreviewOrientation;
  refreshKey: number;
  scale: number;
  template: string;
  previewHandle: string | null;
}) {
  const spec = specFor(device, orientation);
  const outerW = spec.inner.w + spec.bezel * 2;
  const outerH = spec.inner.h + spec.bezel * 2;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);

  // Latest staged template — pushed live so the preview reflects unsaved
  // edits as you type (Shopify "see-as-you-type"). PreviewApp already
  // handles the `state:update` envelope.
  const { template: staged } = useStagedDraft();
  const stagedRef = useRef(staged);
  useEffect(() => {
    stagedRef.current = staged;
  });

  // Two-way selection sync with the preview (P-Highlight). PreviewApp
  // already handles section:highlight/block:highlight (admin→preview)
  // and emits section:select/block:select (preview→admin).
  const selection = useSelectionStore((s) => s.selection);
  const selectionRef = useRef(selection);
  useEffect(() => {
    selectionRef.current = selection;
  });

  // Inspect/Interact mode (Shopify-style). In Interact the preview's
  // tap-to-select handler is off so taps navigate (→ P-DynamicNav).
  const interactive = usePreviewStore((s) => s.interactive);
  const interactiveRef = useRef(interactive);
  useEffect(() => {
    interactiveRef.current = interactive;
  });

  useEffect(() => {
    readyRef.current = false;
    const sendViewport = () => {
      postToIframe(iframeRef, {
        v: 1,
        kind: 'viewport:set',
        width: spec.inner.w,
        height: spec.inner.h,
      });
    };
    const sendState = () => {
      if (stagedRef.current) {
        postToIframe(iframeRef, {
          v: 1,
          kind: 'state:update',
          templateBlob: stagedRef.current,
        });
      }
    };
    // section:highlight clears the active block in PreviewApp, so always
    // send section first, then block.
    const sendHighlight = () => {
      const sel = selectionRef.current;
      postToIframe(iframeRef, {
        v: 1,
        kind: 'section:highlight',
        sectionId: sel.sectionId,
      });
      postToIframe(iframeRef, {
        v: 1,
        kind: 'block:highlight',
        blockId: sel.blockId,
      });
    };
    const sendMode = () => {
      postToIframe(iframeRef, {
        v: 1,
        kind: 'preview:mode',
        interactive: interactiveRef.current,
      });
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.v !== 1) return;
      if (data.kind === 'ready' && event.source === iframeRef.current?.contentWindow) {
        readyRef.current = true;
        sendViewport();
        sendState();
        sendHighlight();
        sendMode();
      }
      // preview → admin: tapping a section/block in the iframe selects it
      // in the editor (zustand getState avoids a stale-closure resubscribe).
      if (data.kind === 'section:select' && typeof data.sectionId === 'string') {
        useSelectionStore.getState().selectSection(data.sectionId);
      }
      if (data.kind === 'block:select' && typeof data.blockId === 'string') {
        const sid = useSelectionStore.getState().selection.sectionId;
        if (sid) useSelectionStore.getState().selectBlock(sid, data.blockId);
      }
      // preview → admin: navigating to a product/collection inside the
      // preview switches the editor to that page's GENERIC template, while
      // the specific handle drives only the preview iframe (Shopify model:
      // edit `collection`, preview a specific collection). P-DynamicNav.
      if (data.kind === 'preview:navigated' && typeof data.route === 'string') {
        // Shop tab isn't a page template — tapping it (or a /shop/<cat>
        // drill-in) in the preview opens the Shop-tab settings panel
        // directly, instead of making the merchant find it under Theme.
        if (data.route === 'shop') {
          useSelectionStore.getState().setViewMode('theme-settings');
          useSelectionStore.getState().setThemeTab('shopTab');
          return;
        }
        // Navigating away from Shop in the preview: drop the Shop-tab
        // panel back to the Sections editor so the editor follows the
        // page being previewed. Gated on themeTab==='shopTab' so a
        // merchant who manually opened Colors/Typography etc. and is
        // browsing the preview is NOT yanked out of their theme view.
        {
          const sel = useSelectionStore.getState();
          if (sel.viewMode === 'theme-settings' && sel.themeTab === 'shopTab') {
            sel.setViewMode('sections');
          }
        }
        const handle =
          typeof data.handle === 'string' && data.handle ? data.handle : undefined;
        const target =
          data.route === 'product'
            ? 'product'
            : data.route === 'collection'
              ? 'collection'
              : 'index';
        // Only write previewHandle when the message carries one (a real
        // user navigation) or it's an explicit home. The handle-less boot
        // message (templateParam-derived, no handle) must NOT clobber the
        // handle already in the iframe URL — that would reload-loop.
        if (data.route === 'home') {
          usePreviewStore.getState().setPreviewHandle(null);
        } else if (handle) {
          usePreviewStore.getState().setPreviewHandle(handle);
        }
        if (useSelectionStore.getState().template !== target) {
          requestTemplateSwitch(target);
        }
      }
    };

    window.addEventListener('message', onMessage);

    const id = setInterval(() => {
      if (readyRef.current) return;
      sendViewport();
    }, 200);
    const timeout = setTimeout(() => clearInterval(id), 1500);

    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(id);
      clearTimeout(timeout);
    };
  }, [spec.inner.w, spec.inner.h, refreshKey]);

  // Debounced live push: whenever the staged copy changes, send it to the
  // (already-ready) iframe without a reload.
  useEffect(() => {
    if (!staged) return;
    const id = setTimeout(() => {
      if (readyRef.current) {
        postToIframe(iframeRef, {
          v: 1,
          kind: 'state:update',
          templateBlob: staged,
        });
      }
    }, 300);
    return () => clearTimeout(id);
  }, [staged]);

  // Inspect/Interact mode → tell the preview to enable/disable its
  // tap-to-select handler. NOT gated on readyRef: the toggle happens
  // long after boot (iframe is listening), and the readyRef handshake
  // can false-negative; the ready handler also resyncs the current mode.
  useEffect(() => {
    postToIframe(iframeRef, {
      v: 1,
      kind: 'preview:mode',
      interactive,
    });
  }, [interactive]);

  // Tree selection → outline it in the preview (section first; it clears
  // the block in PreviewApp, then we set the block).
  useEffect(() => {
    if (!readyRef.current) return;
    postToIframe(iframeRef, {
      v: 1,
      kind: 'section:highlight',
      sectionId: selection.sectionId,
    });
    postToIframe(iframeRef, {
      v: 1,
      kind: 'block:highlight',
      blockId: selection.blockId,
    });
  }, [selection.sectionId, selection.blockId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        width: outerW * scale,
      }}
    >
      <div
        style={{
          width: outerW,
          height: outerH,
          background: '#000',
          borderRadius: spec.radius,
          padding: spec.bezel,
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
        }}
      >
        <iframe
          ref={iframeRef}
          key={refreshKey}
          title={`Preview · ${spec.label}`}
          src={previewSrc(template, previewHandle)}
          style={{
            width: spec.inner.w,
            height: spec.inner.h,
            border: 'none',
            borderRadius: spec.radius - spec.bezel + 4,
            background: '#fff',
            display: 'block',
          }}
        />
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--p-color-text-subdued)',
          marginTop: outerH * (scale - 1),
        }}
      >
        {spec.label} · {spec.inner.w}×{spec.inner.h}
        {orientation === 'landscape' ? ' (landscape)' : ''}
      </div>
    </div>
  );
}

function useFitScale(
  stageRef: React.RefObject<HTMLDivElement | null>,
  totalW: number,
  totalH: number,
  padding = 48,
): number {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const sw = (rect.width - padding) / totalW;
      const sh = (rect.height - padding) / totalH;
      const next = Math.max(0.3, Math.min(1, Math.min(sw, sh)));
      setScale(Number.isFinite(next) ? next : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [stageRef, totalW, totalH, padding]);

  return scale;
}

export function PreviewPane() {
  const device = usePreviewStore((s) => s.device);
  const orientation = usePreviewStore((s) => s.orientation);
  const setDevice = usePreviewStore((s) => s.setDevice);
  const setOrientation = usePreviewStore((s) => s.setOrientation);
  const refreshKey = usePreviewStore((s) => s.refreshKey);
  const refresh = usePreviewStore((s) => s.refresh);
  const interactive = usePreviewStore((s) => s.interactive);
  const setInteractive = usePreviewStore((s) => s.setInteractive);
  const previewHandle = usePreviewStore((s) => s.previewHandle);
  const template = useSelectionStore((s) => s.template);

  const stageRef = useRef<HTMLDivElement>(null);

  // "Both" mode is always portrait-only (per merchant decision).
  const effectiveOrientation: PreviewOrientation =
    device === 'both' ? 'portrait' : orientation;

  // Compute footprint for scale-to-fit.
  const phoneSpec = specFor('phone', effectiveOrientation);
  const tabletSpec = specFor('tablet', effectiveOrientation);
  const phoneOuter = {
    w: phoneSpec.inner.w + phoneSpec.bezel * 2,
    h: phoneSpec.inner.h + phoneSpec.bezel * 2,
  };
  const tabletOuter = {
    w: tabletSpec.inner.w + tabletSpec.bezel * 2,
    h: tabletSpec.inner.h + tabletSpec.bezel * 2,
  };

  const targetSize =
    device === 'phone'
      ? phoneOuter
      : device === 'tablet'
        ? tabletOuter
        : {
            // Both is always portrait, side-by-side.
            w:
              DEVICES_PORTRAIT.phone.inner.w +
              DEVICES_PORTRAIT.phone.bezel * 2 +
              DEVICES_PORTRAIT.tablet.inner.w +
              DEVICES_PORTRAIT.tablet.bezel * 2 +
              32,
            h: Math.max(
              DEVICES_PORTRAIT.phone.inner.h + DEVICES_PORTRAIT.phone.bezel * 2,
              DEVICES_PORTRAIT.tablet.inner.h + DEVICES_PORTRAIT.tablet.bezel * 2,
            ),
          };

  const scale = useFitScale(stageRef, targetSize.w, targetSize.h);

  return (
    <main
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--p-color-bg)',
        overflow: 'hidden',
      }}
    >
      <Box padding="300" borderBlockEndWidth="025" borderColor="border">
        <InlineStack align="space-between" blockAlign="center" gap="300">
          <InlineStack gap="300" blockAlign="center">
            <ButtonGroup variant="segmented">
              <Button
                pressed={device === 'phone'}
                onClick={() => setDevice('phone')}
                icon={MobileIcon}
              >
                Phone
              </Button>
              <Button
                pressed={device === 'tablet'}
                onClick={() => setDevice('tablet')}
                icon={TabletIcon}
              >
                Tablet
              </Button>
              <Button
                pressed={device === 'both'}
                onClick={() => setDevice('both')}
                icon={ViewIcon}
              >
                Both
              </Button>
            </ButtonGroup>
            {device !== 'both' && (
              <ButtonGroup variant="segmented">
                <Button
                  pressed={orientation === 'portrait'}
                  onClick={() => setOrientation('portrait')}
                  icon={RotateLeftIcon}
                >
                  Portrait
                </Button>
                <Button
                  pressed={orientation === 'landscape'}
                  onClick={() => setOrientation('landscape')}
                  icon={RotateRightIcon}
                >
                  Landscape
                </Button>
              </ButtonGroup>
            )}
          </InlineStack>
          <InlineStack gap="300" blockAlign="center">
            <ButtonGroup variant="segmented">
              <Button
                pressed={!interactive}
                onClick={() => setInteractive(false)}
                icon={SelectIcon}
              >
                Inspect
              </Button>
              <Button
                pressed={interactive}
                onClick={() => setInteractive(true)}
                icon={CursorIcon}
              >
                Interact
              </Button>
            </ButtonGroup>
            <Button icon={RefreshIcon} onClick={refresh} accessibilityLabel="Refresh preview">
              Refresh
            </Button>
          </InlineStack>
        </InlineStack>
      </Box>
      <div
        ref={stageRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 32 * scale,
            padding: 24,
          }}
        >
          {device === 'phone' && (
            <DeviceFrame
              device="phone"
              orientation={effectiveOrientation}
              refreshKey={refreshKey}
              scale={scale}
              template={template}
              previewHandle={previewHandle}
            />
          )}
          {device === 'tablet' && (
            <DeviceFrame
              device="tablet"
              orientation={effectiveOrientation}
              refreshKey={refreshKey}
              scale={scale}
              template={template}
              previewHandle={previewHandle}
            />
          )}
          {device === 'both' && (
            <>
              <DeviceFrame
                device="phone"
                orientation="portrait"
                refreshKey={refreshKey}
                scale={scale}
                template={template}
                previewHandle={previewHandle}
              />
              <DeviceFrame
                device="tablet"
                orientation="portrait"
                refreshKey={refreshKey}
                scale={scale}
                template={template}
                previewHandle={previewHandle}
              />
            </>
          )}
        </div>
      </div>
    </main>
  );
}
